<template>
  <DialogTemplate
    style="--dialog-content-padding: 0; --clr-black-1: transparent"
    data-gwmenu="noclose"
  >
    <div>
      <transition-group
        tag="div"
        style="background-color: var(--palette-paper-elevation4)"
      >
        <div
          v-if="dialogState === 'confirm'"
          class="confirm-dialog d-flex flex-column ga-8 justify-center align-center"
        >
          <div
            class="mdi icon_btn mdi-close close"
            @click="closeDialog"
          ></div>

          <div class="text">
            <div>
              {{ $t('user_settings.credits_buy') }}
              <span>{{ Number(props.dialog.credits).toLocaleString() }} {{ PowerCredits }}</span>
              <span>&nbsp;${{ props.dialog.price }}</span
              >?
            </div>
          </div>
          <div class="confirm">
            <div
              v-if="safeActiveCard"
              class="input-cards"
            >
              <component
                :is="iconMap[safeActiveCard.card_network]"
                class="mr-1"
              />
              <span>Bank X {{ safeActiveCard.card_network }}</span>
              <div class="ml-auto">
                <span>**** </span>
                <span>{{ safeActiveCard.last4 }}</span>
              </div>
            </div>

            <GwBtn
              style="width: 100%; justify-content: center; height: 48px; font-size: 16px"
              variant="btn_primary-v2-large"
              @click="confirmPurchase"
            >
              {{ $t('user_settings.dialog.confirm_purchase') }}
            </GwBtn>
          </div>
          <GwBtn
            variant="btn_text_grey_white"
            style="color: rgba(255, 255, 255, 0.7)"
            @click="changePaymentMethod"
          >
            {{ $t('user_settings.dialog.change_payment_method') }}
            <i
              class="mdi-chevron-right mdi"
              style="font-size: 1.5em"
            />
          </GwBtn>
        </div>

        <div
          v-if="dialogState === 'process'"
          style="width: 100vw; height: 100vh; position: fixed; top: 0; left: 0"
          class="d-flex flex-column ga-2 justify-center align-center"
        >
          <LoaderSpinner style="--clr-spinner: var(--palette-primary-main)" />

          <div
            class="d-flex align-center pa-2"
            style="
              background-color: var(--palette-paper-elevation4);
              border-radius: var(--radius-med);
            "
          >
            <i
              class="mdi mdi-information-outline mr-2 mb-auto text-large"
              style="color: var(--palette-info-main)"
            ></i>
            <span> {{ $t('user_settings.dialog.processing_payment') }} </span>
          </div>
        </div>

        <div
          v-if="dialogState === 'failure'"
          class="failure-dialog d-flex flex-column ga-8 justify-center align-center pa-5"
        >
          <div
            class="mdi icon_btn mdi-close close"
            @click="closeDialog"
          ></div>

          <div class="d-flex align-start ga-3">
            <component
              :is="exclamationTriangle"
              :style="{ color: 'var(--palette-error-main)', width: '48px', height: '48px' }"
            />
            <div>
              <span style="font: var(--font-subtitle1)">
                {{ $t('user_settings.dialog.payment_failed') }}
              </span>
              <p style="font: var(--font-body1)">
                {{ $t('user_settings.dialog.try_again_note') }}
              </p>
            </div>
          </div>

          <div class="d-flex ga-2 w-100 justify-space-between">
            <GwBtn
              variant="btn_primary-v2-large"
              style="
                --surface-bg: var(--palette-secondary-main);
                --text-clr: var(--palette-text-secondary);
                height: 48px;
              "
              @click="confirmPurchase"
            >
              <template #default>
                <div class="failure-dialog_btn">{{ $t('user_settings.dialog.try_again') }}</div>
              </template>
            </GwBtn>
            <GwBtn
              variant="btn_primary-v2-large"
              style="height: 48px"
              @click="changePaymentMethod"
            >
              <template #default>
                <div class="failure-dialog_btn">
                  {{ $t('user_settings.dialog.use_another_method') }}
                </div>
              </template>
            </GwBtn>
          </div>
        </div>
      </transition-group>
    </div>
  </DialogTemplate>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { PowerCredits } from '@/helpers/constants'

import { useStore } from 'vuex'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import DialogTemplate from '@/components/Dialogs/DialogTemplate.vue'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner.vue'
import exclamationTriangle from '@/assets/icons/exclamation-triangle.svg'
import { useRouter } from 'vue-router'
import { useAdyenActiveCard } from '@gto/api/vue-query'

import VISA from '@/assets/payment/visa.svg'
import AMERICANEXPRESS from '@/assets/payment/amex.svg'
import UNIONPAY from '@/assets/payment/unionpay.svg'
import MASTERCARD from '@/assets/payment/mastercard.svg'

import { useFetch } from '@/composables/fetch'
import { UserCreditPaymentStatusQuery, type CreditsPaymentStatus } from '@/helpers/constants'
import { sendGaEvent } from '@gto/analytics/google-analytics'

const iconMap = {
  VISA,
  AMERICANEXPRESS,
  UNIONPAY,
  MASTERCARD,
} as const

const { data: activeCard } = useAdyenActiveCard()

const safeActiveCard = computed(() => activeCard.value ?? null)

const store = useStore()
const { fetch } = useFetch()

type DialogState = 'confirm' | 'process' | 'failure'

const dialogState = ref<DialogState>('confirm')

const setDialogState = (state: DialogState): void => {
  dialogState.value = state
}

const router = useRouter()

const changePaymentMethod = () => {
  const dialogKey = router.currentRoute.value.query.dialogs as string

  const match = dialogKey?.match(/gems-dialog_credits-(\d+)_price-(\d+(?:\.\d+)?)/)

  const credits = match ? parseInt(match[1], 10) : 0
  const price = match ? parseFloat(match[2]) : 0

  router.push({
    name: 'CreditsPayment',
    query: {
      credits: credits.toString(),
      price: price.toString(),
    },
  })
}

const props = withDefaults(
  defineProps<{
    dialog: {
      credits: number
      price: number
      discount?: number
    }
  }>(),
  {
    dialog: () => ({
      credits: 0,
      price: 0,
      discount: undefined,
    }),
  },
)

onMounted(() => {
  sendGaEvent({
    event: 'dialog_opened',
    name: 'power_credits',
  })
})

const closeDialog = () => store.dispatch('router_rm_dialog', 'gems-dialog')

async function confirmPurchase() {
  if (!activeCard.value) {
    changePaymentMethod()
    return
  }

  setDialogState('process')

  const postData = {
    credit_amount: props.dialog.credits,
    country_code: activeCard.value?.country_code,
    return_url: window.location.href,
    use_card: true,
  }

  const { data, error } = await fetch({
    conf: {
      endpoint: '/payments/adyen/credits/',
      apiVersion: 'v4',
      method: 'post',
    },
    data: postData,
  })

  if (error || !data) {
    setDialogState('failure')
  } else {
    router.push({
      name: 'UserCredits',
      query: {
        [UserCreditPaymentStatusQuery]: 'successful' as CreditsPaymentStatus,
      },
    })
  }

  closeDialog()
}
</script>

<style scoped lang="scss">
:deep(.dialog_content_close) {
  display: none !important;
}

.confirm-dialog {
  height: 100%;
  border: 1px solid rgba(168, 168, 177, 0.1);
  width: 600px;
  background: linear-gradient(
    23.65deg,
    #16262c 20.32%,
    rgba(22, 38, 44, 0.858559) 46.19%,
    rgba(22, 38, 44, 0.25) 85.54%
  );
  padding: 3rem 1rem;
}
.failure-dialog {
  height: 100%;
  border: 1px solid rgba(168, 168, 177, 0.1);
  width: 500px;
  background: var(--palette-paper-elevation4);
  padding: 3rem 1rem;

  & .failure-dialog_btn {
    font-size: 23px;
    padding: 4px 8px;
  }
}
.close {
  width: 32px;
  height: 32px;
  font-size: 1.5rem;
  position: absolute;
  top: 4px;
  right: 4px;
}

.confirm {
  width: 300px;
}

.text {
  color: var(--text-primary, #fff);
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* typography/h4 */
  font-family: var(--fontFamily, Poppins);
  font-size: var(--_fontSize-2rem, 32px);
  font-style: normal;
  font-weight: var(--fontWeightSemiBold, 600);
  line-height: 123.5%; /* 39.52px */
  letter-spacing: 0.25px;

  span {
    color: var(--primary-main, #aafbb2);
    font-feature-settings:
      'liga' off,
      'clig' off;

    /* typography/h4 */
    font-family: var(--fontFamily, Poppins);
    font-size: var(--_fontSize-2rem, 32px);
    font-style: normal;
    font-weight: var(--fontWeightSemiBold, 600);
    line-height: 123.5%;
    letter-spacing: 0.25px;
  }
}

.discount {
  color: var(--warning-main, #ffa726);
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* typography/body3 */
  font: var(--font-body2);
  font-family: var(--fontFamily, Poppins);
  font-size: var(--font-size-075-rem, 12px);
  font-style: normal;
  font-weight: var(--fontWeightRegular, 400);
  line-height: 143%; /* 17.16px */
  letter-spacing: 0.15px;
  border-radius: 100px;
  border: 1px solid var(--warning-main, #ffa726);
  color: rgb(255, 167, 38);
  padding: 4px 8px;
  border: 1px solid rgb(255, 167, 38);
  border-radius: 100px;
  display: inline-block;
  text-align: center;
}

.input-cards {
  display: flex;
  padding: 8px 16px;
  border-radius: var(--radius-med);
  border: 0.5px solid rgba(255, 255, 255, 0.5);
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
}

.link {
  color: var(--primary-main, #aafbb2);
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* typography/subtitle3 */
  font-family: var(--fontFamily, Poppins);
  font-size: var(--font-size-075-rem, 12px);
  font-style: normal;
  font-weight: var(--fontWeightSemiBold, 600);
  line-height: 157%; /* 18.84px */
  letter-spacing: 0.1px;
  padding: 1rem 1rem 2rem;
}

@media (max-width: 620px) {
  .confirm-dialog {
    height: 100vh;
    justify-content: flex-start;
  }
}

@media (max-width: 1023px) {
  .confirm-dialog {
    width: 100%;
  }
  .failure-dialog {
    width: 100%;

    & .failure-dialog_btn {
      font-size: 16px;
    }
  }
}
</style>
